import React, { useEffect, useState } from "react";
import { Card, Table, Button, Form, Badge, Nav } from "react-bootstrap";
import { getAllAttendance, getComprehensiveAttendance, getMonthlyAttendance } from "../services/attendanceService";
import { getStudents } from "../services/userService";
import Loader from "../components/Loader";
import { STUDENT_LEVELS } from "../utils";
import { toast } from "react-toastify";

export default function Attendance() {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("daily"); // "daily" or "monthly"
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().substring(0, 7) // YYYY-MM format
  );
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (view === "daily") {
      fetchAttendanceData();
    } else {
      fetchMonthlyAttendanceData();
    }
  }, [selectedDate, selectedMonth, view]);

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      // Use comprehensive attendance to include users on leave
      const records = await getComprehensiveAttendance(selectedDate);
      setAttendanceRecords(records);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
      console.error(error);
    }
    setLoading(false);
  };

  const fetchMonthlyAttendanceData = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split("-");
      const stats = await getMonthlyAttendance(parseInt(year), parseInt(month));
      setMonthlyStats(stats);
    } catch (error) {
      toast.error("Failed to fetch monthly attendance data");
      console.error(error);
    }
    setLoading(false);
  };

  const fetchStudents = async () => {
    try {
      const studentData = await getStudents();
      // Filter only employee level students
      const employeeStudents = studentData.filter(
        (student) => student.studentLevel === STUDENT_LEVELS.EMPLOYEE
      );
      setStudents(employeeStudents);
    } catch (error) {
      console.error("Failed to fetch students:", error);
    }
  };

  const getStudentName = (userId) => {
    const student = students.find((s) => s.id === userId);
    return student ? student.name : "Unknown";
  };

  const filteredRecords = attendanceRecords.filter((record) => {
    const studentName = getStudentName(record.userId);
    return (
      studentName.toLowerCase().includes(filter.toLowerCase()) ||
      record.userName.toLowerCase().includes(filter.toLowerCase())
    );
  });

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatHours = (hours) => {
    if (!hours) return "N/A";
    return `${hours} hrs`;
  };

  const getStatusBadge = (record) => {
    if (record.status === "on_leave") {
      return <Badge bg="info">On Leave ({record.leaveType})</Badge>;
    }
    if (!record.checkIn) return <Badge bg="secondary">Not Started</Badge>;
    if (!record.checkOut) return <Badge bg="warning">In Progress</Badge>;
    return <Badge bg="success">Completed</Badge>;
  };

  const getAttendanceStats = () => {
    const totalEmployees = students.length;
    const present = attendanceRecords.filter((r) => r.checkIn && !r.status).length;
    const onLeave = attendanceRecords.filter((r) => r.status === "on_leave").length;
    const absent = totalEmployees - present - onLeave;
    const checkedOut = attendanceRecords.filter((r) => r.checkOut).length;
    
    return { totalEmployees, present, onLeave, absent, checkedOut };
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-2xl font-bold">Employee Attendance</h2>
        <div className="d-flex gap-2">
          <Nav className="gap-2">
            <Nav.Link
              active={view === "daily"}
              onClick={() => setView("daily")}
              className="cursor-pointer"
            >
              Daily View
            </Nav.Link>
            <Nav.Link
              active={view === "monthly"}
              onClick={() => setView("monthly")}
              className="cursor-pointer"
            >
              Monthly View
            </Nav.Link>
          </Nav>
        </div>
      </div>

      {loading && <Loader />}

      {view === "daily" && (
        <>
          <div className="d-flex gap-3 mb-4">
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ width: "200px" }}
            />
            <Form.Control
              type="text"
              placeholder="Search by name..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: "200px" }}
            />
          </div>

          <Card className="shadow">
            <Card.Body>
              <Card.Title>
                Attendance Records for {new Date(selectedDate).toLocaleDateString()}
              </Card.Title>

              {!loading && (
                <div className="mb-3">
                  <small className="text-muted">
                    {(() => {
                      const stats = getAttendanceStats();
                      return `Total Employees: ${stats.totalEmployees} | Present: ${stats.present} | On Leave: ${stats.onLeave} | Absent: ${stats.absent} | Checked Out: ${stats.checkedOut}`;
                    })()}
                  </small>
                </div>
              )}

              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Employee Name</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Total Hours</th>
                    <th>Status</th>
                    <th>Leave Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No attendance records found for this date
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((record, index) => (
                      <tr key={record.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div>
                            <strong>{record.userName}</strong>
                            <br />
                            <small className="text-muted">
                              ID: {record.userId}
                            </small>
                          </div>
                        </td>
                        <td>{formatTime(record.checkIn)}</td>
                        <td>{formatTime(record.checkOut)}</td>
                        <td>{formatHours(record.totalHours)}</td>
                        <td>{getStatusBadge(record)}</td>
                        <td>
                          {record.status === "on_leave" ? (
                            <div>
                              <small className="text-muted">
                                <strong>Type:</strong> {record.leaveType}
                                <br />
                                <strong>Reason:</strong> {record.leaveReason || "N/A"}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* Summary Statistics */}
              {!loading && filteredRecords.length > 0 && (
                <div className="mt-4 p-3 bg-light rounded">
                  <h6>Daily Summary</h6>
                  <div className="row">
                    {(() => {
                      const stats = getAttendanceStats();
                      return (
                        <>
                          <div className="col-md-2">
                            <strong>Total Employees:</strong> {stats.totalEmployees}
                          </div>
                          <div className="col-md-2">
                            <strong>Present:</strong> {stats.present}
                          </div>
                          <div className="col-md-2">
                            <strong>On Leave:</strong> {stats.onLeave}
                          </div>
                          <div className="col-md-2">
                            <strong>Absent:</strong> {stats.absent}
                          </div>
                          <div className="col-md-2">
                            <strong>Checked Out:</strong> {stats.checkedOut}
                          </div>
                          <div className="col-md-2">
                            <strong>Avg Hours:</strong>{" "}
                            {filteredRecords.filter((r) => r.totalHours > 0).length > 0
                              ? Math.round(
                                  (filteredRecords
                                    .filter((r) => r.totalHours > 0)
                                    .reduce((sum, r) => sum + r.totalHours, 0) /
                                    filteredRecords.filter((r) => r.totalHours > 0).length) *
                                    100
                                ) /
                                  100 +
                                " hrs"
                              : "N/A"}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {view === "monthly" && (
        <>
          <div className="mb-4">
            <Form.Control
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{ width: "200px" }}
            />
          </div>

          {monthlyStats && (
            <>
              <Card className="shadow mb-4">
                <Card.Body>
                  <Card.Title>
                    Monthly Summary - {new Date(monthlyStats.year, monthlyStats.month - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </Card.Title>

                  <Table responsive hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Employee Name</th>
                        <th>Present Days</th>
                        <th>Absent Days</th>
                        <th>Leave Days</th>
                        <th>Total Hours</th>
                        <th>Attendance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyStats.userStats.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No attendance records found for this month
                          </td>
                        </tr>
                      ) : (
                        monthlyStats.userStats
                          .filter((stat) =>
                            stat.userName.toLowerCase().includes(filter.toLowerCase())
                          )
                          .map((stat, index) => {
                            const totalWorkingDays = stat.present + stat.absent + stat.onLeave;
                            const attendancePercent = totalWorkingDays > 0
                              ? Math.round((stat.present / totalWorkingDays) * 100)
                              : 0;

                            return (
                              <tr key={stat.userId}>
                                <td>{index + 1}</td>
                                <td>
                                  <strong>{stat.userName}</strong>
                                </td>
                                <td>
                                  <Badge bg="success">{stat.present}</Badge>
                                </td>
                                <td>
                                  <Badge bg="danger">{stat.absent}</Badge>
                                </td>
                                <td>
                                  <Badge bg="info">{stat.onLeave}</Badge>
                                </td>
                                <td>{stat.totalHours.toFixed(2)} hrs</td>
                                <td>
                                  <div>
                                    <Badge
                                      bg={
                                        attendancePercent >= 80
                                          ? "success"
                                          : attendancePercent >= 60
                                          ? "warning"
                                          : "danger"
                                      }
                                    >
                                      {attendancePercent}%
                                    </Badge>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Monthly Statistics Summary */}
              {monthlyStats.userStats.length > 0 && (
                <Card className="shadow">
                  <Card.Body>
                    <Card.Title>Monthly Statistics</Card.Title>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="p-3 bg-light rounded text-center">
                          <h6>Total Employees</h6>
                          <h4 className="text-primary">{monthlyStats.userStats.length}</h4>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="p-3 bg-light rounded text-center">
                          <h6>Avg Attendance %</h6>
                          <h4 className="text-success">
                            {Math.round(
                              monthlyStats.userStats.reduce((sum, stat) => {
                                const totalDays = stat.present + stat.absent + stat.onLeave;
                                return sum + (totalDays > 0 ? (stat.present / totalDays) * 100 : 0);
                              }, 0) / monthlyStats.userStats.length
                            )}%
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="p-3 bg-light rounded text-center">
                          <h6>Total Present Days</h6>
                          <h4 className="text-info">
                            {monthlyStats.userStats.reduce((sum, stat) => sum + stat.present, 0)}
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="p-3 bg-light rounded text-center">
                          <h6>Total Absent Days</h6>
                          <h4 className="text-danger">
                            {monthlyStats.userStats.reduce((sum, stat) => sum + stat.absent, 0)}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
